"use client";

import { motion } from "framer-motion";
import { ConciergeBell, Flower2, HousePlus, Tent } from "lucide-react";

import CustomImage from "@/components/shared/CustomImage";
import Title from "@/components/shared/Title";
import { useLanguage } from "@/contexts/LanguageContext";
import type { About, Media } from "@/payload-types";
import getImageUrl from "@/utils/getImageUrl";
import type { PayLoadData } from "@/utils/payloadUtil";

export default function CoreValue({
  pageData,
}: {
  pageData: PayLoadData<About | undefined> | undefined;
}) {
  const { language } = useLanguage();

  return (
    <section id="our-farm" className="bg-white">
      <div className="container mx-auto px-4">
        <Title
          title={pageData?.[language]?.coreValues.title || ""}
          desc={pageData?.[language]?.coreValues.desc || ""}
        />

        <div className="mx-auto max-w-6xl">
          <div className="flex flex-col-reverse gap-10 lg:flex-row">
            {/* item value */}
            <div className="w-full flex-1 lg:w-1/2">
              <div className="grid grid-cols-2 gap-3 lg:gap-6">
                {pageData?.[language]?.coreValues?.Values.map((item, idx) => {
                  const icons = [Flower2, ConciergeBell, HousePlus, Tent];
                  const IconComponent = icons[idx % icons.length];

                  return (
                    <motion.div
                      key={idx}
                      initial={{ scale: 0 }}
                      whileInView={{ scale: 1 }}
                      viewport={{ once: true }}
                      transition={{
                        duration: 1,
                        ease: "easeOut",
                        delay: idx * 0.1,
                      }}
                      className="group h-full bg-primary-500 px-4 py-8 transition-colors duration-700 hover:bg-primary-600 md:aspect-square md:p-4"
                    >
                      <div className="flex h-full flex-col items-center justify-start gap-4 md:justify-center">
                        <IconComponent
                          width="auto"
                          height={48}
                          stroke="#fff"
                          className="shrink-0 transition-transform duration-700 group-hover:rotate-[360deg]"
                        />
                        <div>
                          <h3 className="text-center text-lg font-bold text-white">
                            {item.title}
                          </h3>
                          <p className="text-center font-light text-white">
                            {item.desc}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>

            <motion.div
              initial={{ y: 100, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: "easeOut" }}
              className="aspect-square w-full flex-1 lg:aspect-[unset] lg:w-1/2"
            >
              {pageData?.[language]?.coreValues?.image && (
                <div className="relative size-full">
                  <CustomImage
                    src={getImageUrl(pageData[language].coreValues?.image)}
                    alt={
                      (pageData[language].coreValues?.image as Media)?.alt ||
                      "image"
                    }
                    fill
                    className="size-full object-cover"
                  />
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
